// Copyright © 2026 Alex Bruce. All rights reserved.
// Proprietary and confidential.

/**
 * Exporting a drawing as whatever file the person actually wants.
 *
 * "Export PNG" decided for them. A save dialog already has a format dropdown —
 * the place people expect to choose — so the button says Export and the choice
 * happens there, with the page generating whichever format came back.
 *
 * PDF is written here rather than pulled in as a dependency. A PDF carrying a
 * single JPEG is a short, completely specified file: JPEG is a PDF-native
 * image encoding, so the bytes go in untouched and the rest is a page, an
 * image object and a cross-reference table.
 */

export type ExportFormat = "png" | "jpeg" | "pdf";

export const EXPORT_TYPES: Array<{ format: ExportFormat; label: string; mime: string; ext: string }> = [
  { format: "png",  label: "PNG image",  mime: "image/png",       ext: ".png" },
  { format: "jpeg", label: "JPEG image", mime: "image/jpeg",      ext: ".jpg" },
  { format: "pdf",  label: "PDF",        mime: "application/pdf", ext: ".pdf" },
];

/** Which format a chosen filename asks for, defaulting to PNG. */
export function formatForFilename(name: string): ExportFormat {
  const lower = String(name || "").toLowerCase();
  if (lower.endsWith(".pdf")) return "pdf";
  if (lower.endsWith(".jpg") || lower.endsWith(".jpeg")) return "jpeg";
  return "png";
}

/** Replaces whatever extension a name has with the one for this format. */
export function withExtension(name: string, format: ExportFormat): string {
  const ext = EXPORT_TYPES.find((t) => t.format === format)!.ext;
  const stem = String(name || "export").replace(/\.(png|jpe?g|pdf)$/i, "");
  return stem + ext;
}

// ── PDF ──────────────────────────────────────────────────────────────────────

function asciiBytes(s: string): number[] {
  const out: number[] = [];
  for (let i = 0; i < s.length; i++) out.push(s.charCodeAt(i) & 0xff);
  return out;
}

/** One page of a PDF: a JPEG and the size to draw it at. */
export interface PdfPage {
  jpeg: Uint8Array;
  /** The image's own pixels. */
  width: number; height: number;
  /**
   * How big the page should be on paper, in points (72 to the inch).
   *
   * Without this a page is as many points as the image has pixels, so a sheet
   * prints at whatever size it happened to be rendered at -- and "1 inch = 20
   * feet" is a claim about paper, so it stops being true. Given the drawing's
   * scale the real width is known exactly, and saying so is what lets a
   * printer be asked for 100% and be right.
   */
  pageW?: number; pageH?: number;
}

/**
 * Wraps JPEGs in a PDF, one page each, at the images' own sizes.
 *
 * Each page is sized in points to match its pixels so nothing is resampled or
 * cropped; a logistics plan is read at whatever size it was drawn, and a
 * surprise letter-size crop would cut the title block off.
 *
 * Several pages because a phased site is several drawings that only mean
 * something in order -- a sequence handed over as five separate files is a
 * sequence somebody will print out of order.
 */
export function jpegsToPdf(pages: PdfPage[]): Uint8Array {
  if (!pages.length) throw new Error("A PDF needs at least one page");

  const bytes: number[] = [];
  const push = (s: string) => bytes.push(...asciiBytes(s));
  const offsets: number[] = [];
  const obj = (num: number, write: () => void) => {
    offsets[num - 1] = bytes.length;
    push(`${num} 0 obj\n`);
    write();
    push("\nendobj\n");
  };

  // Three objects per page -- the page, its content stream and its image --
  // after the catalog and the page tree.
  const pageNum = (i: number) => 3 + i * 3;
  const total = 2 + pages.length * 3;

  push("%PDF-1.4\n");
  // A comment of high bytes marks the file as binary, so tools that sniff
  // text do not mangle the JPEG on the way through.
  bytes.push(0x25, 0xe2, 0xe3, 0xcf, 0xd3, 0x0a);

  obj(1, () => push("<< /Type /Catalog /Pages 2 0 R >>"));
  obj(2, () => push(`<< /Type /Pages /Kids [${
    pages.map((_, i) => `${pageNum(i)} 0 R`).join(" ")}] /Count ${pages.length} >>`));

  pages.forEach((p, i) => {
    const w = Math.max(1, Math.round(p.width));
    const h = Math.max(1, Math.round(p.height));
    // The page is as large as the paper says; the image is stretched to fill
    // it, which is a change of resolution and not a change of scale.
    const pw = Math.max(1, Math.round(p.pageW || w));
    const ph = Math.max(1, Math.round(p.pageH || h));
    const me = pageNum(i), contents = me + 1, image = me + 2;
    const content = `q\n${pw} 0 0 ${ph} 0 0 cm\n/Im0 Do\nQ\n`;

    obj(me, () => push(`<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${pw} ${ph}] `
      + `/Resources << /XObject << /Im0 ${image} 0 R >> >> /Contents ${contents} 0 R >>`));
    obj(contents, () => push(`<< /Length ${content.length} >>\nstream\n${content}endstream`));
    obj(image, () => {
      push(`<< /Type /XObject /Subtype /Image /Width ${w} /Height ${h} `
        + `/ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode `
        + `/Length ${p.jpeg.length} >>\nstream\n`);
      for (let k = 0; k < p.jpeg.length; k++) bytes.push(p.jpeg[k]);
      push("\nendstream");
    });
  });

  const xref = bytes.length;
  push(`xref\n0 ${total + 1}\n`);
  push("0000000000 65535 f \n");
  for (let i = 1; i <= total; i++) push(`${String(offsets[i - 1]).padStart(10, "0")} 00000 n \n`);
  push(`trailer\n<< /Size ${total + 1} /Root 1 0 R >>\nstartxref\n${xref}\n%%EOF\n`);

  return new Uint8Array(bytes);
}

/** The single-page case, which is most of them. */
export function jpegToPdf(jpeg: Uint8Array, width: number, height: number): Uint8Array {
  return jpegsToPdf([{ jpeg, width, height }]);
}

// ── Saving ───────────────────────────────────────────────────────────────────

export interface SaveTarget {
  format: ExportFormat;
  filename: string;
  write: (blob: Blob) => Promise<void>;
}

function downloadTarget(filename: string, format: ExportFormat): SaveTarget {
  return {
    format, filename,
    async write(blob) {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      // Revoked on a turn of the loop: revoking immediately races the download
      // in some browsers and produces an empty file.
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    },
  };
}

/**
 * Asks where to save, and in what format.
 *
 * Uses the system save dialog where the browser has one, which is the whole
 * point: the format dropdown is already there and people know it. Elsewhere
 * the caller's chosen format is used and the file simply downloads.
 */
export async function pickSaveTarget(
  suggestedStem: string,
  fallbackFormat: ExportFormat = "png",
): Promise<SaveTarget | null> {
  const picker = (globalThis as any).showSaveFilePicker;
  if (typeof picker !== "function") {
    return downloadTarget(withExtension(suggestedStem, fallbackFormat), fallbackFormat);
  }

  try {
    // The asked-for format goes first, because the dialog selects the first
    // type it is given: offering PNG first made every save a PNG unless the
    // person changed it, whatever the caller wanted.
    const ordered = [
      ...EXPORT_TYPES.filter((t) => t.format === fallbackFormat),
      ...EXPORT_TYPES.filter((t) => t.format !== fallbackFormat),
    ];
    const handle = await picker({
      suggestedName: withExtension(suggestedStem, fallbackFormat),
      types: ordered.map((t) => ({
        description: t.label,
        accept: { [t.mime]: [t.ext] },
      })),
    });
    const filename: string = handle.name || withExtension(suggestedStem, fallbackFormat);
    return {
      format: formatForFilename(filename),
      filename,
      async write(blob) {
        const writable = await handle.createWritable();
        await writable.write(blob);
        await writable.close();
      },
    };
  } catch (e: any) {
    // Dismissing the dialog is a decision, not a failure.
    if (e?.name === "AbortError") return null;
    return downloadTarget(withExtension(suggestedStem, fallbackFormat), fallbackFormat);
  }
}

/** Renders a canvas to the bytes for one format. */
export async function canvasToBlob(canvas: HTMLCanvasElement, format: ExportFormat): Promise<Blob> {
  if (format === "png") {
    return await new Promise<Blob>((res, rej) =>
      canvas.toBlob((b) => (b ? res(b) : rej(new Error("Could not render the image"))), "image/png"));
  }

  const jpegBlob = await new Promise<Blob>((res, rej) =>
    canvas.toBlob((b) => (b ? res(b) : rej(new Error("Could not render the image"))), "image/jpeg", 0.92));
  if (format === "jpeg") return jpegBlob;

  const jpeg = new Uint8Array(await jpegBlob.arrayBuffer());
  const pdf = jpegToPdf(jpeg, canvas.width, canvas.height);
  // Handed over as its own buffer: the typed-array view is not a BlobPart
  // under the stricter ArrayBuffer typings.
  return new Blob([pdf.buffer.slice(0) as ArrayBuffer], { type: "application/pdf" });
}
